import React, { useState, useEffect, useRef } from 'react';

const NotesModal = ({ isOpen, onClose, onSave, onDelete, noteData, userColor, setModalMessage }) => {
    const [title, setTitle] = useState('');
    const [activeTab, setActiveTab] = useState('editor'); // editor, preview, settings
    const [tags, setTags] = useState([]);
    const [newTag, setNewTag] = useState('');
    const [priority, setPriority] = useState('medium');
    const [category, setCategory] = useState('general');
    const [isPrivate, setIsPrivate] = useState(false);
    const [error, setError] = useState('');
    const [isAnimating, setIsAnimating] = useState(false);
    const [showTagInput, setShowTagInput] = useState(false);
    const contentRef = useRef(null);
    const titleInputRef = useRef(null);
    const tagInputRef = useRef(null);

    // Predefined categories for better organization
    const categories = [
        { id: 'general', name: 'General', icon: 'fas fa-file-alt', color: 'blue' },
        { id: 'urgent', name: 'Urgent', icon: 'fas fa-exclamation-triangle', color: 'red' },
        { id: 'meeting', name: 'Meeting Notes', icon: 'fas fa-users', color: 'green' },
        { id: 'project', name: 'Project', icon: 'fas fa-project-diagram', color: 'purple' },
        { id: 'personal', name: 'Personal', icon: 'fas fa-user', color: 'orange' },
        { id: 'todo', name: 'Todo List', icon: 'fas fa-tasks', color: 'cyan' }
    ];

    useEffect(() => {
        if (noteData) {
            setTitle(noteData.title || '');
            setTags(noteData.tags || []);
            setPriority(noteData.priority || 'medium');
            setCategory(noteData.category || 'general');
            setIsPrivate(noteData.isPrivate || false);
            if (contentRef.current) {
                contentRef.current.innerHTML = noteData.content || '';
            }
        } else {
            // Reset form for new note
            setTitle('');
            setTags([]);
            setPriority('medium');
            setCategory('general');
            setIsPrivate(false);
            if (contentRef.current) {
                contentRef.current.innerHTML = '';
            }
        }
        setError('');
        setActiveTab('editor');
        
        // Focus title input when modal opens
        setTimeout(() => {
            if (titleInputRef.current) {
                titleInputRef.current.focus();
            }
        }, 100);
    }, [noteData, isOpen]);

    // Auto-save functionality (optional)
    useEffect(() => {
        if (isOpen && noteData) {
            const autoSaveInterval = setInterval(() => {
                if (title.trim() && contentRef.current?.innerHTML.trim()) {
                    // Trigger auto-save (you can implement this)
                    console.log('Auto-saving note...');
                }
            }, 30000); // Auto-save every 30 seconds

            return () => clearInterval(autoSaveInterval);
        }
    }, [isOpen, noteData, title]);

    const handleFormat = (command, value = null) => {
        document.execCommand(command, false, value);
        contentRef.current?.focus();
        
        // Add animation feedback
        setIsAnimating(true);
        setTimeout(() => setIsAnimating(false), 200);
    };

    const insertTemplate = (template) => {
        if (!contentRef.current) return;
        
        const templates = {
            meeting: `
                <div style="background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(37, 99, 235, 0.05)); padding: 24px; border-radius: 16px; border: 1px solid rgba(59, 130, 246, 0.2); margin-bottom: 20px;">
                    <h3 style="color: #60a5fa; font-size: 24px; font-weight: bold; margin-bottom: 16px; display: flex; align-items: center;">
                        👥 Meeting Notes - ${new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    </h3>
                    
                    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 24px;">
                        <div style="background: rgba(255, 255, 255, 0.05); padding: 16px; border-radius: 12px; border-left: 4px solid #60a5fa;">
                            <p><strong style="color: #93c5fd;">📅 Date:</strong> ${new Date().toLocaleDateString()}</p>
                            <p><strong style="color: #93c5fd;">⏰ Time:</strong> ${new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</p>
                            <p><strong style="color: #93c5fd;">📍 Location:</strong> [Meeting Room/Virtual]</p>
                        </div>
                        <div style="background: rgba(255, 255, 255, 0.05); padding: 16px; border-radius: 12px; border-left: 4px solid #34d399;">
                            <p><strong style="color: #6ee7b7;">👨‍💼 Host:</strong> [Meeting Host]</p>
                            <p><strong style="color: #6ee7b7;">📝 Type:</strong> [Team Meeting/Client Call]</p>
                            <p><strong style="color: #6ee7b7;">⏱️ Duration:</strong> [Expected Duration]</p>
                        </div>
                    </div>
                    
                    <div style="background: rgba(255, 255, 255, 0.03); padding: 20px; border-radius: 12px; margin-bottom: 16px;">
                        <p><strong style="color: #fbbf24;">🎯 Attendees:</strong></p>
                        <ul style="margin-left: 20px; color: #d1d5db;">
                            <li>✅ [Name] - [Role]</li>
                            <li>✅ [Name] - [Role]</li>
                            <li>❓ [Name] - [Role] (Optional)</li>
                        </ul>
                    </div>
                    
                    <div style="background: rgba(255, 255, 255, 0.03); padding: 20px; border-radius: 12px; margin-bottom: 16px;">
                        <p><strong style="color: #a78bfa;">📋 Agenda:</strong></p>
                        <ol style="margin-left: 20px; color: #d1d5db;">
                            <li><strong>Opening & Welcome</strong> (5 min)</li>
                            <li><strong>Previous Action Items Review</strong> (10 min)</li>
                            <li><strong>Main Discussion Topics</strong> (30 min)</li>
                            <li><strong>Next Steps & Action Items</strong> (10 min)</li>
                            <li><strong>Q&A & Closing</strong> (5 min)</li>
                        </ol>
                    </div>
                </div>
                
                <h3 style="color: #34d399; font-size: 20px; font-weight: bold; margin: 24px 0 12px;">✅ Action Items</h3>
                <div style="background: linear-gradient(135deg, rgba(52, 211, 153, 0.1), rgba(16, 185, 129, 0.05)); padding: 20px; border-radius: 12px; border: 1px solid rgba(52, 211, 153, 0.2);">
                    <ul style="list-style: none; margin-left: 0; color: #d1d5db;">
                        <li style="margin-bottom: 12px; padding: 12px; background: rgba(255, 255, 255, 0.05); border-radius: 8px; border-left: 3px solid #34d399;">
                            <strong style="color: #6ee7b7;">🎯 [Task Description]</strong><br>
                            👤 <em>Assigned to:</em> [Person Name]<br>
                            📅 <em>Due:</em> [Date]<br>
                            🔥 <em>Priority:</em> [High/Medium/Low]
                        </li>
                        <li style="margin-bottom: 12px; padding: 12px; background: rgba(255, 255, 255, 0.05); border-radius: 8px; border-left: 3px solid #fbbf24;">
                            <strong style="color: #fcd34d;">🎯 [Task Description]</strong><br>
                            👤 <em>Assigned to:</em> [Person Name]<br>
                            📅 <em>Due:</em> [Date]<br>
                            🔥 <em>Priority:</em> [High/Medium/Low]
                        </li>
                    </ul>
                </div>
                
                <h3 style="color: #f472b6; font-size: 20px; font-weight: bold; margin: 24px 0 12px;">🚀 Next Steps</h3>
                <div style="background: linear-gradient(135deg, rgba(244, 114, 182, 0.1), rgba(236, 72, 153, 0.05)); padding: 20px; border-radius: 12px; border: 1px solid rgba(244, 114, 182, 0.2);">
                    <p style="color: #f9a8d4; margin-bottom: 16px;"><strong>📍 Follow-up Meeting:</strong> [Date & Time]</p>
                    <p style="color: #f9a8d4; margin-bottom: 16px;"><strong>📧 Summary Distribution:</strong> [When & To Whom]</p>
                    <p style="color: #f9a8d4;"><strong>🔄 Progress Check:</strong> [Schedule for reviewing action items]</p>
                </div>
            `,
            todo: `
                <div style="background: linear-gradient(135deg, rgba(52, 211, 153, 0.1), rgba(16, 185, 129, 0.05)); padding: 24px; border-radius: 16px; border: 1px solid rgba(52, 211, 153, 0.2); margin-bottom: 20px;">
                    <h3 style="color: #34d399; font-size: 24px; font-weight: bold; margin-bottom: 16px; display: flex; align-items: center;">
                        ✅ Epic Todo List - ${new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    </h3>
                    
                    <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 16px; margin-bottom: 24px;">
                        <div style="background: rgba(239, 68, 68, 0.1); padding: 16px; border-radius: 12px; border-left: 4px solid #ef4444;">
                            <h4 style="color: #f87171; font-weight: bold; margin-bottom: 12px;">🔥 HIGH PRIORITY</h4>
                            <ul style="list-style: none; margin-left: 0;">
                                <li style="margin-bottom: 8px; padding: 8px; background: rgba(255, 255, 255, 0.05); border-radius: 6px; display: flex; align-items: center;">
                                    <input type="checkbox" style="margin-right: 8px; transform: scale(1.2);"> 
                                    <span style="color: #fca5a5;">[Critical Task 1]</span>
                                </li>
                                <li style="margin-bottom: 8px; padding: 8px; background: rgba(255, 255, 255, 0.05); border-radius: 6px; display: flex; align-items: center;">
                                    <input type="checkbox" style="margin-right: 8px; transform: scale(1.2);"> 
                                    <span style="color: #fca5a5;">[Critical Task 2]</span>
                                </li>
                            </ul>
                        </div>
                        
                        <div style="background: rgba(251, 191, 36, 0.1); padding: 16px; border-radius: 12px; border-left: 4px solid #fbbf24;">
                            <h4 style="color: #fcd34d; font-weight: bold; margin-bottom: 12px;">⚡ MEDIUM PRIORITY</h4>
                            <ul style="list-style: none; margin-left: 0;">
                                <li style="margin-bottom: 8px; padding: 8px; background: rgba(255, 255, 255, 0.05); border-radius: 6px; display: flex; align-items: center;">
                                    <input type="checkbox" style="margin-right: 8px; transform: scale(1.2);"> 
                                    <span style="color: #fde68a;">[Important Task 1]</span>
                                </li>
                                <li style="margin-bottom: 8px; padding: 8px; background: rgba(255, 255, 255, 0.05); border-radius: 6px; display: flex; align-items: center;">
                                    <input type="checkbox" style="margin-right: 8px; transform: scale(1.2);"> 
                                    <span style="color: #fde68a;">[Important Task 2]</span>
                                </li>
                            </ul>
                        </div>
                        
                        <div style="background: rgba(52, 211, 153, 0.1); padding: 16px; border-radius: 12px; border-left: 4px solid #34d399;">
                            <h4 style="color: #6ee7b7; font-weight: bold; margin-bottom: 12px;">🌟 LOW PRIORITY</h4>
                            <ul style="list-style: none; margin-left: 0;">
                                <li style="margin-bottom: 8px; padding: 8px; background: rgba(255, 255, 255, 0.05); border-radius: 6px; display: flex; align-items: center;">
                                    <input type="checkbox" style="margin-right: 8px; transform: scale(1.2);"> 
                                    <span style="color: #a7f3d0;">[Nice to Have Task 1]</span>
                                </li>
                                <li style="margin-bottom: 8px; padding: 8px; background: rgba(255, 255, 255, 0.05); border-radius: 6px; display: flex; align-items: center;">
                                    <input type="checkbox" style="margin-right: 8px; transform: scale(1.2);"> 
                                    <span style="color: #a7f3d0;">[Nice to Have Task 2]</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
                
                <div style="background: linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(124, 58, 237, 0.05)); padding: 20px; border-radius: 12px; border: 1px solid rgba(139, 92, 246, 0.2);">
                    <p><strong style="color: #c4b5fd;">🎯 Goal:</strong> <span style="color: #d1d5db;">[What you want to accomplish today]</span></p>
                    <p><strong style="color: #c4b5fd;">📅 Deadline:</strong> <span style="color: #d1d5db;">${new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString()}</span></p>
                    <p><strong style="color: #c4b5fd;">🚀 Motivation:</strong> <span style="color: #d1d5db;">[Why this matters to you]</span></p>
                </div>
            `,
            project: `
                <div style="background: linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(124, 58, 237, 0.05)); padding: 24px; border-radius: 16px; border: 1px solid rgba(139, 92, 246, 0.2); margin-bottom: 20px;">
                    <h3 style="color: #a78bfa; font-size: 24px; font-weight: bold; margin-bottom: 16px; display: flex; align-items: center;">
                        🚀 Epic Project Blueprint - [PROJECT NAME]
                    </h3>
                    
                    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 24px;">
                        <div style="background: rgba(255, 255, 255, 0.05); padding: 16px; border-radius: 12px;">
                            <p><strong style="color: #c4b5fd;">📊 Project Status:</strong> <span style="background: rgba(251, 191, 36, 0.2); color: #fcd34d; padding: 4px 8px; border-radius: 12px; font-size: 12px;">IN PROGRESS</span></p>
                            <p><strong style="color: #c4b5fd;">📅 Start Date:</strong> ${new Date().toLocaleDateString()}</p>
                            <p><strong style="color: #c4b5fd;">🎯 Target Date:</strong> [End Date]</p>
                        </div>
                        <div style="background: rgba(255, 255, 255, 0.05); padding: 16px; border-radius: 12px;">
                            <p><strong style="color: #c4b5fd;">👥 Team Size:</strong> [Number] members</p>
                            <p><strong style="color: #c4b5fd;">💰 Budget:</strong> $[Amount]</p>
                            <p><strong style="color: #c4b5fd;">🎭 Client:</strong> [Client Name]</p>
                        </div>
                    </div>
                </div>
                
                <h3 style="color: #60a5fa; font-size: 20px; font-weight: bold; margin: 24px 0 12px;">🎯 Project Objective</h3>
                <div style="background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(37, 99, 235, 0.05)); padding: 20px; border-radius: 12px; border: 1px solid rgba(59, 130, 246, 0.2); margin-bottom: 20px;">
                    <p style="color: #93c5fd; font-size: 16px; line-height: 1.6;">[Describe the main goal and what success looks like for this project...]</p>
                </div>
                
                <h3 style="color: #34d399; font-size: 20px; font-weight: bold; margin: 24px 0 12px;">🏆 Key Milestones</h3>
                <div style="background: linear-gradient(135deg, rgba(52, 211, 153, 0.1), rgba(16, 185, 129, 0.05)); padding: 20px; border-radius: 12px; border: 1px solid rgba(52, 211, 153, 0.2); margin-bottom: 20px;">
                    <div style="display: flex; flex-direction: column; gap: 16px;">
                        <div style="display: flex; align-items: center; padding: 12px; background: rgba(255, 255, 255, 0.05); border-radius: 8px; border-left: 4px solid #34d399;">
                            <div style="width: 24px; height: 24px; background: #34d399; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin-right: 12px; color: #000; font-weight: bold;">1</div>
                            <div style="flex: 1;">
                                <strong style="color: #6ee7b7;">[Milestone 1 Title]</strong>
                                <p style="color: #a7f3d0; margin: 4px 0 0; font-size: 14px;">[Description and deliverables]</p>
                            </div>
                            <span style="color: #6ee7b7; font-size: 12px; background: rgba(52, 211, 153, 0.2); padding: 4px 8px; border-radius: 12px;">[Date]</span>
                        </div>
                        <div style="display: flex; align-items: center; padding: 12px; background: rgba(255, 255, 255, 0.05); border-radius: 8px; border-left: 4px solid #fbbf24;">
                            <div style="width: 24px; height: 24px; background: #fbbf24; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin-right: 12px; color: #000; font-weight: bold;">2</div>
                            <div style="flex: 1;">
                                <strong style="color: #fcd34d;">[Milestone 2 Title]</strong>
                                <p style="color: #fde68a; margin: 4px 0 0; font-size: 14px;">[Description and deliverables]</p>
                            </div>
                            <span style="color: #fcd34d; font-size: 12px; background: rgba(251, 191, 36, 0.2); padding: 4px 8px; border-radius: 12px;">[Date]</span>
                        </div>
                        <div style="display: flex; align-items: center; padding: 12px; background: rgba(255, 255, 255, 0.05); border-radius: 8px; border-left: 4px solid #f472b6;">
                            <div style="width: 24px; height: 24px; background: #f472b6; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin-right: 12px; color: #000; font-weight: bold;">3</div>
                            <div style="flex: 1;">
                                <strong style="color: #f9a8d4;">[Milestone 3 Title]</strong>
                                <p style="color: #fbcfe8; margin: 4px 0 0; font-size: 14px;">[Description and deliverables]</p>
                            </div>
                            <span style="color: #f9a8d4; font-size: 12px; background: rgba(244, 114, 182, 0.2); padding: 4px 8px; border-radius: 12px;">[Date]</span>
                        </div>
                    </div>
                </div>
                
                <h3 style="color: #ef4444; font-size: 20px; font-weight: bold; margin: 24px 0 12px;">⚠️ Risks & Mitigation</h3>
                <div style="background: linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(220, 38, 38, 0.05)); padding: 20px; border-radius: 12px; border: 1px solid rgba(239, 68, 68, 0.2);">
                    <ul style="color: #fca5a5; line-height: 1.6;">
                        <li style="margin-bottom: 8px;"><strong>Risk:</strong> [Potential Risk 1] → <em style="color: #a7f3d0;">Mitigation: [How to handle it]</em></li>
                        <li style="margin-bottom: 8px;"><strong>Risk:</strong> [Potential Risk 2] → <em style="color: #a7f3d0;">Mitigation: [How to handle it]</em></li>
                    </ul>
                </div>
            `,
            idea: `
                <div style="background: linear-gradient(135deg, rgba(251, 191, 36, 0.15), rgba(245, 158, 11, 0.05)); padding: 24px; border-radius: 16px; border: 1px solid rgba(251, 191, 36, 0.3); margin-bottom: 20px; position: relative; overflow: hidden;">
                    <div style="position: absolute; top: -50%; right: -50%; width: 100%; height: 200%; background: radial-gradient(circle, rgba(251, 191, 36, 0.1) 0%, transparent 70%); pointer-events: none;"></div>
                    <h3 style="color: #fbbf24; font-size: 24px; font-weight: bold; margin-bottom: 16px; display: flex; align-items: center; position: relative; z-index: 1;">
                        💡 BRILLIANT IDEA SPARK ⚡
                    </h3>
                    
                    <div style="background: rgba(0, 0, 0, 0.2); backdrop-filter: blur(10px); padding: 20px; border-radius: 12px; margin-bottom: 20px; border: 1px solid rgba(251, 191, 36, 0.2); position: relative; z-index: 1;">
                        <h4 style="color: #fcd34d; font-size: 18px; font-weight: bold; margin-bottom: 12px;">🎯 The Core Concept</h4>
                        <p style="color: #fde68a; font-size: 16px; line-height: 1.6; font-style: italic;">[Describe your brilliant idea in one powerful sentence that captures its essence...]</p>
                    </div>
                    
                    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 20px; position: relative; z-index: 1;">
                        <div style="background: rgba(52, 211, 153, 0.1); padding: 16px; border-radius: 12px; border: 1px solid rgba(52, 211, 153, 0.3);">
                            <h4 style="color: #6ee7b7; font-weight: bold; margin-bottom: 8px;">🎬 Problem it Solves</h4>
                            <p style="color: #a7f3d0; font-size: 14px; line-height: 1.5;">[What pain point or challenge does this address?]</p>
                        </div>
                        <div style="background: rgba(244, 114, 182, 0.1); padding: 16px; border-radius: 12px; border: 1px solid rgba(244, 114, 182, 0.3);">
                            <h4 style="color: #f9a8d4; font-weight: bold; margin-bottom: 8px;">🚀 Potential Impact</h4>
                            <p style="color: #fbcfe8; font-size: 14px; line-height: 1.5;">[How could this change things? Who would benefit?]</p>
                        </div>
                    </div>
                </div>
                
                <h3 style="color: #a78bfa; font-size: 20px; font-weight: bold; margin: 24px 0 12px;">🔄 Next Action Steps</h3>
                <div style="background: linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(124, 58, 237, 0.05)); padding: 20px; border-radius: 12px; border: 1px solid rgba(139, 92, 246, 0.2); margin-bottom: 20px;">
                    <div style="display: flex; flex-direction: column; gap: 12px;">
                        <div style="display: flex; align-items: center; padding: 12px; background: rgba(255, 255, 255, 0.05); border-radius: 8px;">
                            <div style="width: 8px; height: 8px; background: #34d399; border-radius: 50%; margin-right: 12px;"></div>
                            <strong style="color: #c4b5fd;">Research Phase:</strong>
                            <span style="color: #e5e7eb; margin-left: 8px;">[Market research, competitor analysis, feasibility study]</span>
                        </div>
                        <div style="display: flex; align-items: center; padding: 12px; background: rgba(255, 255, 255, 0.05); border-radius: 8px;">
                            <div style="width: 8px; height: 8px; background: #fbbf24; border-radius: 50%; margin-right: 12px;"></div>
                            <strong style="color: #c4b5fd;">Planning Phase:</strong>
                            <span style="color: #e5e7eb; margin-left: 8px;">[Create detailed roadmap, resource planning, timeline]</span>
                        </div>
                        <div style="display: flex; align-items: center; padding: 12px; background: rgba(255, 255, 255, 0.05); border-radius: 8px;">
                            <div style="width: 8px; height: 8px; background: #f472b6; border-radius: 50%; margin-right: 12px;"></div>
                            <strong style="color: #c4b5fd;">Implementation:</strong>
                            <span style="color: #e5e7eb; margin-left: 8px;">[MVP development, testing, iteration]</span>
                        </div>
                    </div>
                </div>
                
                <div style="background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(37, 99, 235, 0.05)); padding: 16px; border-radius: 12px; border: 1px solid rgba(59, 130, 246, 0.2); text-align: center;">
                    <p style="color: #93c5fd; font-weight: bold; font-size: 14px;">💫 "The best time to plant a tree was 20 years ago. The second best time is now." 💫</p>
                </div>
            `,
            brainstorm: `
                <div style="background: linear-gradient(135deg, rgba(99, 102, 241, 0.15), rgba(79, 70, 229, 0.05)); padding: 24px; border-radius: 16px; border: 1px solid rgba(99, 102, 241, 0.3); margin-bottom: 20px;">
                    <h3 style="color: #818cf8; font-size: 24px; font-weight: bold; margin-bottom: 16px; display: flex; align-items: center;">
                        🧠 MIND STORM SESSION ⚡ - [TOPIC/CHALLENGE]
                    </h3>
                    
                    <div style="background: rgba(0, 0, 0, 0.3); padding: 20px; border-radius: 12px; margin-bottom: 20px;">
                        <h4 style="color: #c7d2fe; font-weight: bold; margin-bottom: 12px;">🎯 Challenge Statement</h4>
                        <p style="color: #e0e7ff; font-size: 16px; line-height: 1.6; font-style: italic; text-align: center; padding: 16px; border: 2px dashed rgba(99, 102, 241, 0.3); border-radius: 8px;">
                            "How might we [define the problem or opportunity you're brainstorming about]?"
                        </p>
                    </div>
                </div>
                
                <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 20px; margin-bottom: 24px;">
                    <div style="background: linear-gradient(135deg, rgba(34, 197, 94, 0.1), rgba(22, 163, 74, 0.05)); padding: 20px; border-radius: 12px; border: 1px solid rgba(34, 197, 94, 0.2);">
                        <h4 style="color: #4ade80; font-weight: bold; margin-bottom: 16px; display: flex; align-items: center;">
                            <span style="margin-right: 8px;">🌱</span> WILD IDEAS
                        </h4>
                        <div style="space-y: 8px;">
                            <div style="background: rgba(255, 255, 255, 0.05); padding: 12px; border-radius: 8px; margin-bottom: 8px; border-left: 3px solid #4ade80;">
                                <p style="color: #86efac;">💫 [Crazy idea #1 - no limits!]</p>
                            </div>
                            <div style="background: rgba(255, 255, 255, 0.05); padding: 12px; border-radius: 8px; margin-bottom: 8px; border-left: 3px solid #4ade80;">
                                <p style="color: #86efac;">🚀 [Out-of-the-box thinking #2]</p>
                            </div>
                            <div style="background: rgba(255, 255, 255, 0.05); padding: 12px; border-radius: 8px; margin-bottom: 8px; border-left: 3px solid #4ade80;">
                                <p style="color: #86efac;">🎭 [Creative solution #3]</p>
                            </div>
                        </div>
                    </div>
                    
                    <div style="background: linear-gradient(135deg, rgba(251, 191, 36, 0.1), rgba(245, 158, 11, 0.05)); padding: 20px; border-radius: 12px; border: 1px solid rgba(251, 191, 36, 0.2);">
                        <h4 style="color: #fbbf24; font-weight: bold; margin-bottom: 16px; display: flex; align-items: center;">
                            <span style="margin-right: 8px;">⚡</span> PRACTICAL IDEAS
                        </h4>
                        <div style="space-y: 8px;">
                            <div style="background: rgba(255, 255, 255, 0.05); padding: 12px; border-radius: 8px; margin-bottom: 8px; border-left: 3px solid #fbbf24;">
                                <p style="color: #fcd34d;">🎯 [Feasible approach #1]</p>
                            </div>
                            <div style="background: rgba(255, 255, 255, 0.05); padding: 12px; border-radius: 8px; margin-bottom: 8px; border-left: 3px solid #fbbf24;">
                                <p style="color: #fcd34d;">💼 [Business-ready solution #2]</p>
                            </div>
                            <div style="background: rgba(255, 255, 255, 0.05); padding: 12px; border-radius: 8px; margin-bottom: 8px; border-left: 3px solid #fbbf24;">
                                <p style="color: #fcd34d;">📊 [Data-driven idea #3]</p>
                            </div>
                        </div>
                    </div>
                    
                    <div style="background: linear-gradient(135deg, rgba(244, 114, 182, 0.1), rgba(236, 72, 153, 0.05)); padding: 20px; border-radius: 12px; border: 1px solid rgba(244, 114, 182, 0.2);">
                        <h4 style="color: #f472b6; font-weight: bold; margin-bottom: 16px; display: flex; align-items: center;">
                            <span style="margin-right: 8px;">💎</span> HYBRID IDEAS
                        </h4>
                        <div style="space-y: 8px;">
                            <div style="background: rgba(255, 255, 255, 0.05); padding: 12px; border-radius: 8px; margin-bottom: 8px; border-left: 3px solid #f472b6;">
                                <p style="color: #f9a8d4;">🔗 [Combine idea A + B]</p>
                            </div>
                            <div style="background: rgba(255, 255, 255, 0.05); padding: 12px; border-radius: 8px; margin-bottom: 8px; border-left: 3px solid #f472b6;">
                                <p style="color: #f9a8d4;">🎨 [Creative twist on existing solution]</p>
                            </div>
                            <div style="background: rgba(255, 255, 255, 0.05); padding: 12px; border-radius: 8px; margin-bottom: 8px; border-left: 3px solid #f472b6;">
                                <p style="color: #f9a8d4;">⚙️ [Improved version of current process]</p>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div style="background: linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(124, 58, 237, 0.05)); padding: 20px; border-radius: 12px; border: 1px solid rgba(139, 92, 246, 0.2);">
                    <h4 style="color: #a78bfa; font-weight: bold; margin-bottom: 16px;">🏆 TOP 3 WINNERS</h4>
                    <div style="display: flex; flex-direction: column; gap: 12px;">
                        <div style="display: flex; align-items: center; padding: 16px; background: rgba(255, 215, 0, 0.1); border-radius: 12px; border: 1px solid rgba(255, 215, 0, 0.3);">
                            <div style="font-size: 24px; margin-right: 16px;">🥇</div>
                            <div>
                                <strong style="color: #fcd34d;">[Best Idea Title]</strong>
                                <p style="color: #fde68a; font-size: 14px; margin-top: 4px;">[Why this one wins - impact, feasibility, innovation]</p>
                            </div>
                        </div>
                        <div style="display: flex; align-items: center; padding: 16px; background: rgba(192, 192, 192, 0.1); border-radius: 12px; border: 1px solid rgba(192, 192, 192, 0.3);">
                            <div style="font-size: 24px; margin-right: 16px;">🥈</div>
                            <div>
                                <strong style="color: #d1d5db;">[Second Best Idea]</strong>
                                <p style="color: #e5e7eb; font-size: 14px; margin-top: 4px;">[Strong potential with some considerations]</p>
                            </div>
                        </div>
                        <div style="display: flex; align-items: center; padding: 16px; background: rgba(205, 127, 50, 0.1); border-radius: 12px; border: 1px solid rgba(205, 127, 50, 0.3);">
                            <div style="font-size: 24px; margin-right: 16px;">🥉</div>
                            <div>
                                <strong style="color: #cd7f32;">[Third Place Idea]</strong>
                                <p style="color: #d4af8c; font-size: 14px; margin-top: 4px;">[Good backup option or future consideration]</p>
                            </div>
                        </div>
                    </div>
                </div>
            `,
            daily: `
                <div style="background: linear-gradient(135deg, rgba(244, 114, 182, 0.15), rgba(236, 72, 153, 0.05)); padding: 24px; border-radius: 16px; border: 1px solid rgba(244, 114, 182, 0.3); margin-bottom: 20px;">
                    <h3 style="color: #f472b6; font-size: 24px; font-weight: bold; margin-bottom: 16px; display: flex; align-items: center;">
                        📅 Daily Journal - ${new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} ✨
                    </h3>
                    
                    <div style="display: grid; grid-template-columns: 1fr auto; gap: 16px; margin-bottom: 20px; align-items: center;">
                        <div style="background: rgba(0, 0, 0, 0.2); padding: 16px; border-radius: 12px;">
                            <p style="color: #f9a8d4; font-size: 16px;">
                                <strong>🌅 Today's Date:</strong> ${new Date().toLocaleDateString()}<br>
                                <strong>🌤️ Weather/Mood:</strong> [How are you feeling today?]<br>
                                <strong>⏰ Time:</strong> ${new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                            </p>
                        </div>
                        <div style="font-size: 48px; opacity: 0.7;">
                            ${['😊', '🌟', '💫', '✨', '🔥', '💖'][Math.floor(Math.random() * 6)]}
                        </div>
                    </div>
                </div>
                
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 24px;">
                    <div style="background: linear-gradient(135deg, rgba(52, 211, 153, 0.1), rgba(16, 185, 129, 0.05)); padding: 20px; border-radius: 12px; border: 1px solid rgba(52, 211, 153, 0.2);">
                        <h4 style="color: #34d399; font-weight: bold; margin-bottom: 16px; display: flex; align-items: center;">
                            <span style="margin-right: 8px;">🎯</span> Today's Wins
                        </h4>
                        <div style="space-y: 12px;">
                            <div style="background: rgba(255, 255, 255, 0.05); padding: 12px; border-radius: 8px; border-left: 3px solid #34d399;">
                                <p style="color: #6ee7b7;">🏆 [Accomplishment #1]</p>
                            </div>
                            <div style="background: rgba(255, 255, 255, 0.05); padding: 12px; border-radius: 8px; border-left: 3px solid #34d399;">
                                <p style="color: #6ee7b7;">💪 [Something you're proud of]</p>
                            </div>
                            <div style="background: rgba(255, 255, 255, 0.05); padding: 12px; border-radius: 8px; border-left: 3px solid #34d399;">
                                <p style="color: #6ee7b7;">✨ [Small victory that mattered]</p>
                            </div>
                        </div>
                    </div>
                    
                    <div style="background: linear-gradient(135deg, rgba(251, 191, 36, 0.1), rgba(245, 158, 11, 0.05)); padding: 20px; border-radius: 12px; border: 1px solid rgba(251, 191, 36, 0.2);">
                        <h4 style="color: #fbbf24; font-weight: bold; margin-bottom: 16px; display: flex; align-items: center;">
                            <span style="margin-right: 8px;">📚</span> Lessons Learned
                        </h4>
                        <div style="space-y: 12px;">
                            <div style="background: rgba(255, 255, 255, 0.05); padding: 12px; border-radius: 8px; border-left: 3px solid #fbbf24;">
                                <p style="color: #fcd34d;">💡 [Key insight from today]</p>
                            </div>
                            <div style="background: rgba(255, 255, 255, 0.05); padding: 12px; border-radius: 8px; border-left: 3px solid #fbbf24;">
                                <p style="color: #fcd34d;">🔄 [What would you do differently?]</p>
                            </div>
                            <div style="background: rgba(255, 255, 255, 0.05); padding: 12px; border-radius: 8px; border-left: 3px solid #fbbf24;">
                                <p style="color: #fcd34d;">📖 [Knowledge gained]</p>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div style="background: linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(124, 58, 237, 0.05)); padding: 20px; border-radius: 12px; border: 1px solid rgba(139, 92, 246, 0.2); margin-bottom: 20px;">
                    <h4 style="color: #a78bfa; font-weight: bold; margin-bottom: 16px;">💭 Today's Thoughts & Reflections</h4>
                    <div style="background: rgba(0, 0, 0, 0.2); padding: 20px; border-radius: 8px; min-height: 120px; border: 2px dashed rgba(139, 92, 246, 0.3);">
                        <p style="color: #c4b5fd; font-style: italic; text-align: center; opacity: 0.7;">
                            [Write about your day, your feelings, interesting conversations, random thoughts, creative ideas, or anything that's on your mind...]
                        </p>
                    </div>
                </div>
                
                <div style="background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(37, 99, 235, 0.05)); padding: 20px; border-radius: 12px; border: 1px solid rgba(59, 130, 246, 0.2);">
                    <h4 style="color: #60a5fa; font-weight: bold; margin-bottom: 16px;">🚀 Tomorrow's Intentions</h4>
                    <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px;">
                        <div style="text-align: center; padding: 16px; background: rgba(255, 255, 255, 0.05); border-radius: 8px;">
                            <div style="font-size: 24px; margin-bottom: 8px;">🎯</div>
                            <strong style="color: #93c5fd;">Priority Goal</strong>
                            <p style="color: #bfdbfe; font-size: 14px; margin-top: 4px;">[Main focus for tomorrow]</p>
                        </div>
                        <div style="text-align: center; padding: 16px; background: rgba(255, 255, 255, 0.05); border-radius: 8px;">
                            <div style="font-size: 24px; margin-bottom: 8px;">💖</div>
                            <strong style="color: #93c5fd;">Self Care</strong>
                            <p style="color: #bfdbfe; font-size: 14px; margin-top: 4px;">[How will you nurture yourself?]</p>
                        </div>
                        <div style="text-align: center; padding: 16px; background: rgba(255, 255, 255, 0.05); border-radius: 8px;">
                            <div style="font-size: 24px; margin-bottom: 8px;">✨</div>
                            <strong style="color: #93c5fd;">Something Fun</strong>
                            <p style="color: #bfdbfe; font-size: 14px; margin-top: 4px;">[What will bring you joy?]</p>
                        </div>
                    </div>
                </div>
            `
        };
        
        contentRef.current.innerHTML = templates[template] || '';
        contentRef.current.focus();
        
        // Epic Animation Sequence
        setIsAnimating(true);
        
        // Create floating success notification
        const notification = document.createElement('div');
        notification.innerHTML = `
            <div style="
                position: fixed; 
                top: 20px; 
                right: 20px; 
                background: linear-gradient(135deg, rgba(34, 197, 94, 0.9), rgba(22, 163, 74, 0.8)); 
                color: white; 
                padding: 16px 24px; 
                border-radius: 12px; 
                box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3); 
                z-index: 10000;
                backdrop-filter: blur(10px);
                border: 1px solid rgba(255, 255, 255, 0.2);
                animation: slideInRight 0.5s ease-out, fadeOut 0.5s ease-out 2.5s;
            ">
                <div style="display: flex; align-items: center; space-x: 8px;">
                    <span style="font-size: 20px;">🔥</span>
                    <span style="font-weight: bold;">Template Loaded!</span>
                    <span style="font-size: 16px;">Ready to customize ✨</span>
                </div>
            </div>
        `;
        document.body.appendChild(notification);
        
        // Remove notification after 3 seconds
        setTimeout(() => {
            if (document.body.contains(notification)) {
                document.body.removeChild(notification);
            }
        }, 3000);
        
        setTimeout(() => setIsAnimating(false), 500);
    };

    const addTag = () => {
        if (newTag.trim() && !tags.includes(newTag.trim())) {
            setTags([...tags, newTag.trim()]);
            setNewTag('');
            setShowTagInput(false);
        }
    };

    const removeTag = (tagToRemove) => {
        setTags(tags.filter(tag => tag !== tagToRemove));
    };

    const handleSave = () => {
        setError('');
        if (!title.trim()) {
            setError('Note title is required');
            setActiveTab('editor');
            titleInputRef.current?.focus();
            return;
        }
        
        if (!contentRef.current?.innerHTML.trim() || contentRef.current.innerHTML === '<br>') {
            setError('Note content cannot be empty');
            setActiveTab('editor');
            contentRef.current?.focus();
            return;
        }

        const noteDataToSave = {
            id: noteData?.id,
            title: title.trim(),
            content: contentRef.current.innerHTML,
            tags,
            priority,
            category,
            isPrivate,
            lastModified: new Date().toISOString()
        };

        onSave(noteDataToSave);
        onClose();
    };

    const handleDelete = () => {
        if (noteData?.id) {
            onDelete(noteData.id);
        }
    };

    const getColorForCategory = (categoryId) => {
        const cat = categories.find(c => c.id === categoryId);
        return cat ? cat.color : 'blue';
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'high': return 'text-red-400 bg-red-500/20';
            case 'medium': return 'text-yellow-400 bg-yellow-500/20';
            case 'low': return 'text-green-400 bg-green-500/20';
            default: return 'text-gray-400 bg-gray-500/20';
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
            <div className="glass-card w-full max-w-6xl h-[90vh] flex flex-col rounded-2xl shadow-2xl border border-white/20 animate-bounceIn overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-white/10 bg-gradient-to-r from-blue-500/5 to-purple-500/5">
                    <div className="flex items-center space-x-4">
                        <div className="relative group">
                            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/30 to-purple-500/30 rounded-xl blur-md group-hover:blur-lg transition-all animate-pulse"></div>
                            <div className="relative w-12 h-12 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-xl flex items-center justify-center backdrop-blur-sm border border-blue-400/30">
                                <i className="fas fa-sticky-note text-xl text-blue-400 animate-float"></i>
                            </div>
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold gradient-text">
                                {noteData ? 'Edit Note' : 'Create New Note'}
                            </h2>
                            <p className="text-gray-400 text-sm">
                                {noteData ? 'Modify your existing note' : 'Capture your thoughts and ideas'}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-3 hover:bg-red-500/20 text-red-400 rounded-xl transition-all duration-300 transform hover:scale-110 hover:rotate-90 group"
                    >
                        <i className="fas fa-times text-lg group-hover:animate-spin"></i>
                    </button>
                </div>

                {/* Tab Navigation */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 bg-white/5">
                    <div className="flex space-x-2">
                        {[
                            { id: 'editor', label: 'Editor', icon: 'fas fa-edit' },
                            { id: 'preview', label: 'Preview', icon: 'fas fa-eye' },
                            { id: 'settings', label: 'Settings', icon: 'fas fa-cog' }
                        ].map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`px-4 py-2 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 flex items-center space-x-2 ${
                                    activeTab === tab.id
                                        ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30 animate-glow'
                                        : 'text-gray-400 hover:text-white hover:bg-white/10'
                                }`}
                            >
                                <i className={`${tab.icon} text-sm`}></i>
                                <span>{tab.label}</span>
                            </button>
                        ))}
                    </div>
                    
                    {/* Quick Actions */}
                    <div className="flex items-center space-x-2">
                        <div className={`px-3 py-1 rounded-full text-xs font-semibold ${getPriorityColor(priority)}`}>
                            {priority.charAt(0).toUpperCase() + priority.slice(1)} Priority
                        </div>
                        {isPrivate && (
                            <div className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-500/20 text-purple-400 flex items-center space-x-1">
                                <i className="fas fa-lock"></i>
                                <span>Private</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-hidden">
                    {activeTab === 'editor' && (
                        <div className="h-full flex flex-col">
                            {/* Title Input */}
                            <div className="p-6 border-b border-white/10">
                                <input
                                    ref={titleInputRef}
                                    type="text"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    placeholder="Enter your note title..."
                                    className="w-full text-2xl font-bold bg-transparent border-none outline-none text-white placeholder-gray-400 focus:placeholder-gray-500 transition-all"
                                />
                            </div>

                            {/* Toolbar */}
                            <div className="px-6 py-4 border-b border-white/10 bg-white/5">
                                <div className="flex flex-wrap items-center gap-2">
                                    {/* Formatting Buttons */}
                                    <div className="flex items-center space-x-1 mr-4">
                                        {[
                                            { cmd: 'bold', icon: 'fas fa-bold', title: 'Bold' },
                                            { cmd: 'italic', icon: 'fas fa-italic', title: 'Italic' },
                                            { cmd: 'underline', icon: 'fas fa-underline', title: 'Underline' }
                                        ].map((format) => (
                                            <button
                                                key={format.cmd}
                                                onClick={() => handleFormat(format.cmd)}
                                                className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-gray-300 hover:text-white transition-all transform hover:scale-110"
                                                title={format.title}
                                            >
                                                <i className={format.icon}></i>
                                            </button>
                                        ))}
                                    </div>

                                    <div className="w-px h-6 bg-white/20 mx-2"></div>

                                    {/* List Buttons */}
                                    <div className="flex items-center space-x-1 mr-4">
                                        <button
                                            onClick={() => handleFormat('insertUnorderedList')}
                                            className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-gray-300 hover:text-white transition-all transform hover:scale-110"
                                            title="Bullet List"
                                        >
                                            <i className="fas fa-list-ul"></i>
                                        </button>
                                        <button
                                            onClick={() => handleFormat('insertOrderedList')}
                                            className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-gray-300 hover:text-white transition-all transform hover:scale-110"
                                            title="Numbered List"
                                        >
                                            <i className="fas fa-list-ol"></i>
                                        </button>
                                    </div>

                                    <div className="w-px h-6 bg-white/20 mx-2"></div>

                                    {/* ULTRA HOT Template Buttons */}
                                    <div className="flex items-center space-x-2">
                                        {[
                                            { 
                                                id: 'meeting', 
                                                icon: 'fas fa-users', 
                                                title: 'Meeting Template',
                                                color: 'from-blue-500/20 to-cyan-500/20',
                                                hoverColor: 'hover:from-blue-500/40 hover:to-cyan-500/40',
                                                textColor: 'text-blue-400 hover:text-blue-200',
                                                borderColor: 'border-blue-500/30 hover:border-blue-400/60',
                                                shadowColor: 'hover:shadow-blue-500/30',
                                                emoji: '👥'
                                            },
                                            { 
                                                id: 'todo', 
                                                icon: 'fas fa-tasks', 
                                                title: 'Todo Template',
                                                color: 'from-green-500/20 to-emerald-500/20',
                                                hoverColor: 'hover:from-green-500/40 hover:to-emerald-500/40',
                                                textColor: 'text-green-400 hover:text-green-200',
                                                borderColor: 'border-green-500/30 hover:border-green-400/60',
                                                shadowColor: 'hover:shadow-green-500/30',
                                                emoji: '✅'
                                            },
                                            { 
                                                id: 'project', 
                                                icon: 'fas fa-project-diagram', 
                                                title: 'Project Template',
                                                color: 'from-purple-500/20 to-pink-500/20',
                                                hoverColor: 'hover:from-purple-500/40 hover:to-pink-500/40',
                                                textColor: 'text-purple-400 hover:text-purple-200',
                                                borderColor: 'border-purple-500/30 hover:border-purple-400/60',
                                                shadowColor: 'hover:shadow-purple-500/30',
                                                emoji: '🚀'
                                            },
                                            { 
                                                id: 'idea', 
                                                icon: 'fas fa-lightbulb', 
                                                title: 'Idea Template',
                                                color: 'from-yellow-500/20 to-orange-500/20',
                                                hoverColor: 'hover:from-yellow-500/40 hover:to-orange-500/40',
                                                textColor: 'text-yellow-400 hover:text-yellow-200',
                                                borderColor: 'border-yellow-500/30 hover:border-yellow-400/60',
                                                shadowColor: 'hover:shadow-yellow-500/30',
                                                emoji: '💡'
                                            },
                                            { 
                                                id: 'brainstorm', 
                                                icon: 'fas fa-brain', 
                                                title: 'Brainstorm Template',
                                                color: 'from-indigo-500/20 to-violet-500/20',
                                                hoverColor: 'hover:from-indigo-500/40 hover:to-violet-500/40',
                                                textColor: 'text-indigo-400 hover:text-indigo-200',
                                                borderColor: 'border-indigo-500/30 hover:border-indigo-400/60',
                                                shadowColor: 'hover:shadow-indigo-500/30',
                                                emoji: '🧠'
                                            },
                                            { 
                                                id: 'daily', 
                                                icon: 'fas fa-calendar-day', 
                                                title: 'Daily Journal Template',
                                                color: 'from-rose-500/20 to-red-500/20',
                                                hoverColor: 'hover:from-rose-500/40 hover:to-red-500/40',
                                                textColor: 'text-rose-400 hover:text-rose-200',
                                                borderColor: 'border-rose-500/30 hover:border-rose-400/60',
                                                shadowColor: 'hover:shadow-rose-500/30',
                                                emoji: '📅'
                                            }
                                        ].map((template, index) => (
                                            <div
                                                key={template.id}
                                                className="group relative"
                                                style={{ animationDelay: `${index * 0.1}s` }}
                                            >
                                                {/* Pulsing Background Effect */}
                                                <div className={`absolute inset-0 bg-gradient-to-r ${template.color} rounded-xl blur-md opacity-0 group-hover:opacity-100 transition-all duration-500 animate-pulse scale-110`}></div>
                                                
                                                {/* Main Button */}
                                                <button
                                                    onClick={() => insertTemplate(template.id)}
                                                    className={`relative group p-3 rounded-xl bg-gradient-to-r ${template.color} ${template.hoverColor} ${template.textColor} transition-all duration-500 transform hover:scale-125 hover:-translate-y-1 hover:rotate-3 border-2 ${template.borderColor} hover:shadow-xl ${template.shadowColor} animate-fadeIn overflow-hidden`}
                                                    title={template.title}
                                                >
                                                    {/* Shimmer Effect */}
                                                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000 ease-out"></div>
                                                    
                                                    {/* Icon Container */}
                                                    <div className="relative z-10 flex items-center justify-center">
                                                        <i className={`${template.icon} text-lg transition-all duration-300 group-hover:scale-110 group-hover:animate-bounce`}></i>
                                                    </div>
                                                    
                                                    {/* Emoji Badge */}
                                                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-sm animate-float border border-white/30 group-hover:animate-spin group-hover:scale-125 transition-all duration-300">
                                                        {template.emoji}
                                                    </div>
                                                    
                                                    {/* Floating Particles Effect */}
                                                    <div className="absolute inset-0 pointer-events-none">
                                                        <div className="absolute top-1 left-1 w-1 h-1 bg-white/40 rounded-full animate-ping opacity-0 group-hover:opacity-100" style={{ animationDelay: '0.1s' }}></div>
                                                        <div className="absolute top-2 right-2 w-1 h-1 bg-white/40 rounded-full animate-ping opacity-0 group-hover:opacity-100" style={{ animationDelay: '0.3s' }}></div>
                                                        <div className="absolute bottom-1 left-2 w-1 h-1 bg-white/40 rounded-full animate-ping opacity-0 group-hover:opacity-100" style={{ animationDelay: '0.5s' }}></div>
                                                        <div className="absolute bottom-2 right-1 w-1 h-1 bg-white/40 rounded-full animate-ping opacity-0 group-hover:opacity-100" style={{ animationDelay: '0.7s' }}></div>
                                                    </div>
                                                </button>
                                                
                                                {/* Super Hot Tooltip */}
                                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-black/80 backdrop-blur-xl text-white text-sm rounded-lg border border-white/20 opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none whitespace-nowrap z-50 animate-slideDown">
                                                    <div className="flex items-center space-x-2">
                                                        <span>{template.emoji}</span>
                                                        <span className="font-semibold">{template.title}</span>
                                                    </div>
                                                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black/80"></div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Content Editor */}
                            <div className="flex-1 p-6 overflow-y-auto">
                                <div
                                    ref={contentRef}
                                    contentEditable
                                    className={`w-full h-full min-h-[300px] outline-none text-gray-200 leading-relaxed transition-all ${isAnimating ? 'animate-pulse' : ''}`}
                                    style={{
                                        fontSize: '16px',
                                        lineHeight: '1.6'
                                    }}
                                    placeholder="Start writing your note here..."
                                />
                            </div>
                        </div>
                    )}

                    {activeTab === 'preview' && (
                        <div className="h-full p-6 overflow-y-auto">
                            <div className="max-w-4xl mx-auto">
                                <h1 className="text-3xl font-bold text-white mb-6 border-b border-white/20 pb-4">
                                    {title || 'Untitled Note'}
                                </h1>
                                <div 
                                    className="text-gray-200 prose prose-invert max-w-none"
                                    dangerouslySetInnerHTML={{ __html: contentRef.current?.innerHTML || '<p class="text-gray-400 italic">No content yet...</p>' }}
                                />
                            </div>
                        </div>
                    )}

                    {activeTab === 'settings' && (
                        <div className="h-full p-6 overflow-y-auto">
                            <div className="max-w-2xl mx-auto space-y-6">
                                {/* Category Selection */}
                                <div className="glass-card rounded-xl p-6">
                                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                                        <i className="fas fa-folder-open mr-2 text-blue-400"></i>
                                        Category
                                    </h3>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                        {categories.map((cat) => (
                                            <button
                                                key={cat.id}
                                                onClick={() => setCategory(cat.id)}
                                                className={`p-3 rounded-lg border transition-all transform hover:scale-105 flex flex-col items-center space-y-2 ${
                                                    category === cat.id
                                                        ? `bg-${cat.color}-500/20 border-${cat.color}-500/50 text-${cat.color}-300`
                                                        : 'bg-white/5 border-white/10 text-gray-400 hover:text-white hover:bg-white/10'
                                                }`}
                                            >
                                                <i className={`${cat.icon} text-lg`}></i>
                                                <span className="text-sm font-medium">{cat.name}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Priority Selection */}
                                <div className="glass-card rounded-xl p-6">
                                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                                        <i className="fas fa-flag mr-2 text-orange-400"></i>
                                        Priority Level
                                    </h3>
                                    <div className="grid grid-cols-3 gap-3">
                                        {[
                                            { id: 'low', name: 'Low', color: 'green', icon: 'fas fa-chevron-down' },
                                            { id: 'medium', name: 'Medium', color: 'yellow', icon: 'fas fa-minus' },
                                            { id: 'high', name: 'High', color: 'red', icon: 'fas fa-chevron-up' }
                                        ].map((prio) => (
                                            <button
                                                key={prio.id}
                                                onClick={() => setPriority(prio.id)}
                                                className={`p-3 rounded-lg border transition-all transform hover:scale-105 flex items-center justify-center space-x-2 ${
                                                    priority === prio.id
                                                        ? `bg-${prio.color}-500/20 border-${prio.color}-500/50 text-${prio.color}-300`
                                                        : 'bg-white/5 border-white/10 text-gray-400 hover:text-white hover:bg-white/10'
                                                }`}
                                            >
                                                <i className={prio.icon}></i>
                                                <span className="font-medium">{prio.name}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Tags Management */}
                                <div className="glass-card rounded-xl p-6">
                                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                                        <i className="fas fa-tags mr-2 text-green-400"></i>
                                        Tags
                                    </h3>
                                    <div className="flex flex-wrap gap-2 mb-4">
                                        {tags.map((tag, index) => (
                                            <span
                                                key={index}
                                                className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm flex items-center space-x-1 animate-slideInLeft"
                                            >
                                                <span>{tag}</span>
                                                <button
                                                    onClick={() => removeTag(tag)}
                                                    className="text-blue-400 hover:text-red-400 transition-colors ml-1"
                                                >
                                                    <i className="fas fa-times text-xs"></i>
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                    
                                    {showTagInput ? (
                                        <div className="flex space-x-2">
                                            <input
                                                ref={tagInputRef}
                                                type="text"
                                                value={newTag}
                                                onChange={(e) => setNewTag(e.target.value)}
                                                onKeyPress={(e) => e.key === 'Enter' && addTag()}
                                                placeholder="Enter tag name..."
                                                className="flex-1 px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500/50"
                                                autoFocus
                                            />
                                            <button
                                                onClick={addTag}
                                                className="px-4 py-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-all"
                                            >
                                                <i className="fas fa-plus"></i>
                                            </button>
                                            <button
                                                onClick={() => setShowTagInput(false)}
                                                className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-all"
                                            >
                                                <i className="fas fa-times"></i>
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            onClick={() => setShowTagInput(true)}
                                            className="px-4 py-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-all transform hover:scale-105"
                                        >
                                            <i className="fas fa-plus mr-2"></i>
                                            Add Tag
                                        </button>
                                    )}
                                </div>

                                {/* Privacy Settings */}
                                <div className="glass-card rounded-xl p-6">
                                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                                        <i className="fas fa-shield-alt mr-2 text-purple-400"></i>
                                        Privacy Settings
                                    </h3>
                                    <label className="flex items-center space-x-3 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={isPrivate}
                                            onChange={(e) => setIsPrivate(e.target.checked)}
                                            className="sr-only"
                                        />
                                        <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all ${
                                            isPrivate ? 'bg-purple-500/20 border-purple-500 text-purple-400' : 'border-gray-500 text-transparent hover:border-purple-400'
                                        }`}>
                                            <i className="fas fa-check text-sm"></i>
                                        </div>
                                        <span className="text-gray-300">Make this note private</span>
                                    </label>
                                    <p className="text-gray-400 text-sm mt-2">
                                        Private notes are only visible to you
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between p-6 border-t border-white/10 bg-white/5">
                    {error && (
                        <div className="flex items-center space-x-2 text-red-400 animate-slideInLeft">
                            <i className="fas fa-exclamation-circle"></i>
                            <span className="text-sm">{error}</span>
                        </div>
                    )}
                    
                    <div className={`flex items-center space-x-4 ${error ? 'ml-auto' : ''}`}>
                        {noteData && (
                            <button
                                onClick={handleDelete}
                                className="px-6 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 font-semibold rounded-xl transition-all duration-300 transform hover:scale-105 border border-red-500/30"
                            >
                                <i className="fas fa-trash-alt mr-2"></i>
                                Delete
                            </button>
                        )}
                        
                        <button
                            onClick={onClose}
                            className="px-6 py-2 bg-gray-500/20 hover:bg-gray-500/30 text-gray-400 font-semibold rounded-xl transition-all duration-300 transform hover:scale-105"
                        >
                            Cancel
                        </button>
                        
                        <button
                            onClick={handleSave}
                            className={`px-8 py-2 btn-gradient text-white font-semibold rounded-xl transition-all duration-300 transform hover:scale-105 animate-glow ${
                                isAnimating ? 'animate-pulse' : ''
                            }`}
                        >
                            <i className="fas fa-save mr-2"></i>
                            {noteData ? 'Update Note' : 'Save Note'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NotesModal;
